import { getSuiteApiBase, getSuiteBasePath, readUserId } from "../suiteSession";

export type CoffreEntry = {
  name: string;
  path: string;
  kind: "folder" | "file" | "shortcut";
  size?: number | null;
};

function headers(): HeadersInit {
  const h: Record<string, string> = {};
  const uid = readUserId();
  if (uid) h["X-User-Id"] = uid;
  return h;
}

function qs(extra: Record<string, string> = {}): string {
  const q = new URLSearchParams(extra);
  const uid = readUserId();
  if (uid) q.set("suite_user_id", uid);
  const s = q.toString();
  return s ? `?${s}` : "";
}

async function coffreJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${getSuiteApiBase()}/${path.replace(/^\//, "")}`, {
    credentials: "include",
    cache: "no-store",
    ...init,
    headers: { ...headers(), ...(init.headers as Record<string, string> | undefined) },
  });
  const data = (await res.json().catch(() => ({}))) as T & { success?: boolean; message?: string; error?: string };
  if (!res.ok || data.success === false) {
    throw new Error(data.message || data.error || `Coffre HTTP ${res.status}`);
  }
  return data;
}

export async function coffreStatus(): Promise<{ unlocked: boolean; message?: string }> {
  try {
    const data = await coffreJson<{
      server_unlocked?: boolean | number | string;
      message?: string;
    }>(`coffre-status.php${qs()}`);
    const unlocked = data.server_unlocked === true || data.server_unlocked === 1 || data.server_unlocked === "1";
    return { unlocked, message: data.message };
  } catch (e) {
    return { unlocked: false, message: e instanceof Error ? e.message : "Coffre indisponible." };
  }
}

export async function coffreList(path = "", zone = "vault"): Promise<CoffreEntry[]> {
  const data = await coffreJson<{ entries?: CoffreEntry[] }>(
    `coffre-list.php${qs({ path, zone, ...(zone === "picker" ? { folders_only: "1" } : {}) })}`,
  );
  return data.entries ?? [];
}

export async function coffreSearch(q: string): Promise<CoffreEntry[]> {
  const data = await coffreJson<{ entries?: CoffreEntry[] }>(
    `coffre-search.php${qs({ q, zone: "vault", kind: "file", limit: "40" })}`,
  );
  return data.entries ?? [];
}

export async function coffreFile(path: string): Promise<File> {
  const res = await fetch(`${getSuiteApiBase()}/coffre-download.php${qs({ path, stream: "1", zone: "vault" })}`, {
    credentials: "include",
    headers: headers(),
  });
  if (!res.ok) {
    let msg = "Lecture Coffre impossible.";
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) msg = body.message;
    } catch {
      /* binaire */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  if (blob.size <= 0) throw new Error("Fichier Coffre vide.");
  const name = path.split("/").filter(Boolean).pop() || "document.pdf";
  return new File([blob], name, { type: blob.type || "application/pdf" });
}

export async function coffreUpload(folderPath: string, file: File): Promise<void> {
  const body = new FormData();
  body.set("path", folderPath);
  body.set("zone", "vault");
  body.set("file", file, file.name);
  await coffreJson("coffre-upload.php", { method: "POST", body });
}

function facturxApi(): string {
  return `${getSuiteBasePath()}/facturx/api`;
}

export type FactureClient = {
  id: number;
  company_id: number;
  name: string;
  company_name?: string;
  siret?: string;
};

export async function facturxPickClients(q = ""): Promise<FactureClient[]> {
  const query = new URLSearchParams({ action: "pick" });
  if (q.trim()) query.set("q", q.trim());
  const res = await fetch(`${facturxApi()}/client-files.php?${query}`, {
    credentials: "include",
    cache: "no-store",
    headers: headers(),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    clients?: FactureClient[];
    message?: string;
  };
  if (!res.ok || data.ok === false) {
    throw new Error(data.message || "Impossible de lire les clients Factures.");
  }
  return data.clients ?? [];
}

export async function facturxSendPdf(client: FactureClient, file: File): Promise<void> {
  const body = new FormData();
  body.set("company_id", String(client.company_id));
  body.set("client_id", String(client.id));
  body.set("file", file, file.name || "dossier-signe.pdf");
  const res = await fetch(`${facturxApi()}/client-files.php`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: headers(),
    body,
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string };
  if (!res.ok || data.ok === false) {
    throw new Error(data.message || "Classement dans Factures impossible.");
  }
}
